import type { SiteRagReport } from '../types/siteRag';

/**
 * Remove gen-time layout snapshots from template-linked reports so live
 * template resolution always wins (cover, section visibility, columns).
 *
 * COVER FIX — the strip is now GATED on a LIVE template having actually
 * been resolved (`hasLiveTemplate === true`). Previously the strip fired
 * UNCONDITIONALLY whenever `templateId` was truthy, deleting the report's
 * `coverLayout` even when the linked template row could NOT be resolved
 * this session (still hydrating, private, or cross-org). With the layout
 * snapshot gone AND no live template, `resolveCoverLayout` fell through to
 * the Hero default — so an assigned Masthead/Classic cover was silently
 * lost. Now:
 *   - When a live template IS resolved, the layout snapshots are stripped
 *     so the template wins (unchanged behaviour).
 *   - When NO live template is resolved, the snapshots are KEPT (the
 *     report-level cover survives), and the gen-time `coverLayout` is
 *     mirrored onto `coverLayoutAtGen` so `resolveCoverLayout` can fall
 *     back to it before defaulting to Hero.
 *
 * The `coverLayoutAtGen` snapshot is preserved in BOTH branches so it
 * survives a later strip too.
 */
export function stripTemplateLinkedLayoutSnapshots<
  T extends Pick<SiteRagReport, 'templateId'>,
>(report: T, hasLiveTemplate = true): T {
  if (!report.templateId?.trim()) return report;
  const next = { ...report } as T & {
    coverLayout?: unknown;
    coverLayoutAtGen?: unknown;
    sectionVisibility?: unknown;
    sectionColumnLayouts?: unknown;
  };

  // Preserve the gen-time cover layout as a durable snapshot so an
  // unresolved-template render can fall back to it (never lose the
  // assigned Masthead/Classic to a Hero default).
  if (next.coverLayoutAtGen == null && next.coverLayout != null) {
    next.coverLayoutAtGen = next.coverLayout;
  }

  // Only strip the live-overridable snapshots once a LIVE template is in
  // hand to supply them; otherwise keep the report-level layout.
  if (hasLiveTemplate) {
    delete next.coverLayout;
    delete next.sectionVisibility;
    delete next.sectionColumnLayouts;
  }
  return next as T;
}
